// utils/elementFormat/pattern.js
//
// A short text's PATTERN (keepr-web docs/plans/data-types-second-pass.md
// §3.4, decision D14 as the owner ruled it on 2026-09-30): the shape a
// `text-small` value must have — a ZIP code, a part number, a plate — held on
// every write path as 400 `pattern { element, message }` (a row error of the
// same code). An element's `options.pattern` is ONE of:
//
//   { preset: 'us-zip', message? }            one of PRESETS below; the author
//                                             may replace its sentence
//   { regex: '[A-Z]{3}-[0-9]{4}', flags?, message }
//                                             a custom JavaScript regex
//
// The regex is IMPLICITLY ANCHORED — the whole value must match, so
// "[0-9]{5}" never passes "a 12345 b" — and `flags` may only be 'i' (letters
// in either case). `message` is the one sentence a person reads when a value
// does not fit (at most 120 characters; a preset brings its own).
//
// A custom regex is refused at authoring (400 `invalid_options`) when it is
// longer than 200 characters, carries another flag, does not compile, or
// fails checkSafety: a star-height rule after the `safe-regex2` package's —
// no OPEN-ENDED repeat inside an open-ended repeated group (star height at
// most 1) and at most 25 quantifiers in all. Only `*`, `+` and `{n,}` count
// toward the height (the owner's ruling B3, 2026-10-01, "loosen"): `?`,
// `{n}` and `{n,m}` repeat a bounded number of times, so `[0-9]{5}(-[0-9]{4})?`
// is accepted. safe-regex2 itself counts every quantifier; that package is
// not a dependency of either repo (the build that wrote this had no network),
// so the rule is implemented here and pinned by test cases in vectors.json.
// The rule alone does not see every slow regex — `(a|a)*b` has star height 1
// and takes seconds on 30 characters, and since B3 so does `(a+){10}y` — so a
// regex the rule passes is then held to checkCost: an upper bound on the
// steps the engine can take checking ANY value the pattern meets, counted
// from the regex alone (no clock, so the same answer on every machine), and
// refused `slow` past MAX_STEPS (KPR-1096; until then this was a timed probe
// in the API). The bound, not the star-height rule, is what keeps a pattern
// cheap; the API keeps one clock of its own as a backstop, a time budget on
// every evaluation (utils/patternRule.js).
//
// PURE AND DEPENDENCY-FREE ON PURPOSE, and vendored byte-identical into
// keepr-web at `src/utils/elementFormat/pattern.js`, so the element editor's
// refusal, the form's message under the field and the API's 400 are one
// rule. MIRRORED in keepr-web — change them together, with the shared vectors
// in `vectors.json` beside this file. Nothing here may require() anything, and
// nothing here may use spread syntax (the web build treats such a file as a
// module).

'use strict';

// The longest custom regex an author may write, in characters, and the
// longest sentence a pattern may carry.
const MAX_REGEX = 200;
const MAX_MESSAGE = 120;
// The only flag: letters in either case.
const ALLOWED_FLAGS = ['i'];
// safe-regex2's defaults: star height 1, 25 quantifiers. The height counts
// open-ended repeats only (B3); the 25 still counts EVERY quantifier, bounded
// ones included (⚑ the B3 builder's call): it caps how much a pattern can ask
// of the engine, which a `{1,100}` does as much as a `+`, and every preset
// and the ruling's own example sit far below it.
const MAX_STAR_HEIGHT = 1;
const MAX_REPETITIONS = 25;

// The predefined patterns an author picks from (the owner's note on D14 ⚑:
// "offer predefined patterns to choose from, beside a custom regex"). Every
// one passes checkPattern — the rule a custom regex is held to — and carries
// no flag, so the collection schema can print it as JSON Schema's `pattern`.
// `hint` is a line the element editor shows under the choice.
const PRESETS = Object.freeze([
	Object.freeze({
		id: 'digits',
		label: 'Digits only',
		regex: '[0-9]+',
		message: 'Use digits only (0–9).',
	}),
	Object.freeze({
		id: 'letters',
		label: 'Letters only',
		regex: '[A-Za-z]+',
		message: 'Use letters only (A–Z), with no spaces.',
	}),
	Object.freeze({
		id: 'us-zip',
		label: 'US ZIP code',
		regex: '[0-9]{5}|[0-9]{5}-[0-9]{4}',
		message: 'Enter a ZIP code like 97301 or 97301-1234.',
	}),
	Object.freeze({
		id: 'us-state',
		label: 'US state code',
		regex: 'AL|AK|AZ|AR|CA|CO|CT|DE|DC|FL|GA|HI|ID|IL|IN|IA|KS|KY|LA|ME|MD|MA|MI|MN|MS|MO|MT|NE|NV|NH|NJ|NM|NY|NC|ND|OH|OK|OR|PA|RI|SC|SD|TN|TX|UT|VT|VA|WA|WV|WI|WY|AS|GU|MP|PR|VI|AA|AE|AP',
		message: 'Enter a two-letter state code in capitals, like OR.',
		hint: 'The 50 states, DC, the territories and military mail codes. Set Case to Capitals and "or" is saved as OR.',
	}),
	Object.freeze({
		id: 'us-phone',
		label: 'US phone number',
		regex: '\\(?[0-9]{3}\\)?[-. ]?[0-9]{3}[-. ]?[0-9]{4}',
		message: 'Enter a 10-digit phone number, like 503-555-0123.',
		hint: 'Checks the shape only. For numbers people call, the Phone type checks and formats them.',
	}),
	Object.freeze({
		id: 'email-like',
		label: 'Email-like',
		regex: '[^@\\s]+@[^@\\s]+\\.[^@\\s]+',
		message: 'Enter an address like name@example.com.',
		hint: 'Checks the shape only. For addresses people write to, the Email type checks them properly.',
	}),
	Object.freeze({
		id: 'license-plate',
		label: 'License plate',
		regex: '[A-Za-z0-9]{1,4}[- ]?[A-Za-z0-9]{1,4}',
		message: 'Enter a plate of up to 8 letters and digits, like ABC-1234.',
	}),
	Object.freeze({
		id: 'vin',
		label: 'Vehicle identification number (VIN)',
		regex: '[A-HJ-NPR-Za-hj-npr-z0-9]{17}',
		message: 'A VIN is 17 letters and digits, without I, O or Q.',
	}),
]);

const PRESET_BY_ID = {};
PRESETS.forEach(function (p) { PRESET_BY_ID[p.id] = p; });

const isPlainObject = v => v !== null && typeof v === 'object' && !Array.isArray(v);
const hasOwn = (obj, key) => Object.prototype.hasOwnProperty.call(obj, key);

/** Characters as a person counts them (code points; an emoji is one). */
function countChars(text) {
	let n = 0;
	const s = String(text);
	for (let i = 0; i < s.length; i++) {
		const c = s.charCodeAt(i);
		if (c >= 0xd800 && c <= 0xdbff && i + 1 < s.length) {
			const d = s.charCodeAt(i + 1);
			if (d >= 0xdc00 && d <= 0xdfff) i++;
		}
		n++;
	}
	return n;
}

/** The preset with this id, or null. */
function presetOf(id) {
	return typeof id === 'string' && hasOwn(PRESET_BY_ID, id) ? PRESET_BY_ID[id] : null;
}

/** The whole value must match: the regex wrapped as ^(?:…)$. */
function anchored(regex) {
	return '^(?:' + regex + ')$';
}

// ------------------------------------------------------------ the safety rule

/**
 * analyzeRegex(source) -> { starHeight, repetitions } | null
 *
 * Reads a JavaScript regex source (no `u` flag) the way `ret` tokenises it
 * for safe-regex2: every quantifier — `*`, `+`, `?`, `{n}`, `{n,}`, `{n,m}`,
 * greedy or lazy — is a REPETITION (counted in `repetitions`). Only an
 * OPEN-ENDED one — `*`, `+`, `{n,}` — adds one to the height of what it
 * repeats (the owner's ruling B3): a group under `?`, `{n}` or `{n,m}` is as
 * high as its deepest repeat inside, no higher. null when the source does
 * not parse (which safe-regex2 also calls unsafe). Call it on a source that
 * COMPILES: it reads structure, it does not re-check syntax.
 */
function analyzeRegex(source) {
	const src = String(source);
	const n = src.length;
	let i = 0;
	let repetitions = 0;

	// A quantifier at i, consumed with its lazy `?`: 'open' for `*`, `+` and
	// `{n,}` (no upper bound), 'bounded' for `?`, `{n}` and `{n,m}`, false
	// when there is none. A `{` that does not open a well-formed {n}, {n,} or
	// {n,m} is a literal.
	function readQuantifier() {
		const c = src[i];
		if (c === '*' || c === '+' || c === '?') {
			i++;
			if (src[i] === '?') i++;
			return c === '?' ? 'bounded' : 'open';
		}
		if (c === '{') {
			// The whole rest of the source: V8 sets no limit on a count's
			// digits, so a slice could read a long count as text (KPR-1096).
			const m = /^\{[0-9]+(,([0-9]*))?\}/.exec(src.slice(i));
			if (!m) return false;
			i += m[0].length;
			if (src[i] === '?') i++;
			// `{n,}` has a comma and nothing after it: open-ended.
			return m[1] !== undefined && m[2] === '' ? 'open' : 'bounded';
		}
		return false;
	}

	function skipEscape() {
		// i is on the backslash.
		const next = src[i + 1];
		if (next === undefined) throw new Error('trailing backslash');
		if (next === 'u' && /^[0-9a-fA-F]{4}$/.test(src.slice(i + 2, i + 6))) { i += 6; return; }
		if (next === 'x' && /^[0-9a-fA-F]{2}$/.test(src.slice(i + 2, i + 4))) { i += 4; return; }
		if (next === 'c' && /^[A-Za-z]$/.test(src[i + 2] || '')) { i += 3; return; }
		i += 2;
	}

	function skipClass() {
		// i is on '['. In JavaScript the first ']' closes the class — `[]` is
		// the empty class and `[^]` any character.
		i++;
		if (src[i] === '^') i++;
		while (i < n && src[i] !== ']') {
			if (src[i] === '\\') skipEscape();
			else i++;
		}
		if (i >= n) throw new Error('unterminated class');
		i++;
	}

	function skipGroupPrefix() {
		// i is just past '('. (?: (?= (?! (?<= (?<! (?<name>
		if (src[i] !== '?') return;
		const next = src[i + 1];
		if (next === ':' || next === '=' || next === '!') { i += 2; return; }
		if (next === '<') {
			const after = src[i + 2];
			if (after === '=' || after === '!') { i += 3; return; }
			const close = src.indexOf('>', i + 2);
			if (close === -1) throw new Error('unterminated group name');
			i = close + 1;
			return;
		}
		throw new Error('unknown group');
	}

	// The deepest repetition in a sequence of alternatives, up to the ')'
	// that closes it (depth > 0) or the end.
	function sequence(depth) {
		let height = 0;
		while (i < n) {
			const c = src[i];
			if (c === ')') {
				if (depth === 0) throw new Error('unbalanced )');
				return height;
			}
			if (c === '|') { i++; continue; }
			let atom = 0;
			if (c === '(') {
				i++;
				skipGroupPrefix();
				atom = sequence(depth + 1);
				if (src[i] !== ')') throw new Error('unterminated group');
				i++;
			} else if (c === '[') {
				skipClass();
			} else if (c === '\\') {
				skipEscape();
			} else if (c === '*' || c === '+' || c === '?') {
				throw new Error('nothing to repeat');
			} else {
				i++;
			}
			const quantifier = readQuantifier();
			if (quantifier) {
				repetitions++;
				if (quantifier === 'open') atom += 1;
			}
			if (atom > height) height = atom;
		}
		if (depth > 0) throw new Error('unterminated group');
		return height;
	}

	try {
		const starHeight = sequence(0);
		return { starHeight: starHeight, repetitions: repetitions };
	} catch (e) {
		return null;
	}
}

/**
 * checkSafety(regex) -> null | 'unsafe'
 *
 * The star-height rule: at most 1, counting open-ended repeats only (B3),
 * and at most 25 quantifiers of any kind; a source that does not parse is
 * unsafe.
 */
function checkSafety(regex) {
	const found = analyzeRegex(regex);
	if (!found) return 'unsafe';
	if (found.starHeight > MAX_STAR_HEIGHT || found.repetitions > MAX_REPETITIONS) return 'unsafe';
	return null;
}

// ------------------------------------------------------------ the cost bound
//
// WHY A BOUND, NOT A CLOCK (KPR-1096). The star-height rule above does not
// see every regex that backtracks badly: `(a|a)*b` has star height 1 and
// takes seconds on thirty characters, and since B3 a counted repeat of a
// repeat, `(a+){10}y`, passes it too. Until KPR-1096 the API ran each custom
// regex against long probe values under a 5 ms clock, so one pattern could
// pass on a quiet machine and be refused `slow` on a busy one, and on
// 2026-10-25 the production host refused the Vehicles bundle's own VIN
// pattern that way. This replaces it: an UPPER BOUND on the work the engine
// can do checking ANY value the pattern meets, counted from the regex text
// alone. The same regex gets the same answer on every machine, every time,
// in the browser and in the API.
//
// WHAT IS COUNTED. A JavaScript regex is matched by backtracking: the engine
// reads one code unit at a time and, wherever the pattern offers a choice (an
// alternative, another pass of a loop, an optional part), tries the choices
// one after another. Its work on a value is the number of choices it tries.
// The regex is read into a COUNTED POSITION AUTOMATON:
//
//   - one position for each code unit a match may read, every copy of a
//     counted repeat its own (`a{3}` is three positions; `{n,m}` is n copies
//     then m-n nested optional ones, at most COST_UNITS of them, the most
//     code units a value holds);
//   - between two positions, the NUMBER of distinct routes the engine can
//     take from one to the other without reading anything: one per
//     alternative, per way round a loop. `(a+)+` has two routes from a back
//     to a, so the routes double at every character;
//   - a route the engine starts and then abandons is charged to a dead
//     position nothing can enter: a pass of a loop that read nothing (the
//     engine refuses it), and a lookaround's own search (its body bounded
//     the same way, a lookbehind read backwards).
//
// Wherever the real engine could stop sooner, the automaton does not: `^`,
// `$`, `\b` and `\B` always pass, a backreference reads as `[^]*` (anything,
// any length), and under the `i` flag a class also holds the other case of
// its ASCII letters, and everything past ASCII if it holds anything past
// ASCII. So every route the engine explores is a distinct route here, and
// the count can only be too high, never too low. (test/patternCost.test.js
// holds it against a backtracking matcher written from the ECMAScript spec,
// on random patterns and every short value: equal where none of the above
// applies, never below.)
//
// THE SEARCH covers every value at once. The pattern's classes split the code
// units into the few kinds no position tells apart; the search walks a value
// one code unit at a time, keeping for each set of live positions some value
// reaches the most routes ANY such value reaches each position by. Past
// MAX_LEVEL_SETS sets they merge (sets that start at the same position, then
// neighbours), which can only add; a walk that then passes the line is walked
// again keeping WIDE_LEVEL_SETS, which counts closer to the truth. The bound
// is the sum over the lengths of the most work any value of that length
// costs. When a length's sets repeat an earlier length's exactly, the rest
// repeats too and is added without walking it. A value holds at most
// COST_CODE_POINTS code points, the short text ceiling; a pattern that cannot
// read a surrogate meets at most that many code units, and one that can meets
// up to twice as many, a surrogate pair being two code units and one code
// point. So the second is walked to twice the length first (cheap, and a
// bound: it lets a value hold more code points than it can), and only when
// that passes the line is it walked again one code point at a time, a pair in
// one level, which is exact about pairs.
//
// THE LINE. One step is one route tried, plus CAPTURE_STEP for each capture
// group boundary it crosses: the engine saves and restores a group's
// registers as it backtracks through it (measured: about 2.3 ns a step with
// no group, rising with each one crossed). A pattern is refused `slow` past
// MAX_STEPS, about 2.5 ms of engine time on the worst value against the
// 20 ms the API allows one evaluation. A pattern too large to count (past
// MAX_POSITIONS positions or MAX_ROUTES routes), or whose count would cost
// more than MAX_COUNTING units of the check's own work, is refused too: the
// check never trusts what it could not count, and nobody can make the check
// itself expensive.

const MAX_STEPS = 1000000;
const CAPTURE_STEP = 0.5;
// The short text ceiling (utils/elementFormat/text.js) in code points: the
// longest value a pattern ever meets, since the length is checked first.
const COST_CODE_POINTS = 255;
const COST_UNITS = 2 * COST_CODE_POINTS;
const MAX_POSITIONS = 4000;
const MAX_ROUTES = 20000;
const MAX_LEVEL_SETS = 32;
const WIDE_LEVEL_SETS = 128;
const MAX_COUNTING = 6000000;
// Counts stop growing here, far past every line, so no sum overflows.
const SATURATE = 1e18;

const saturated = x => (x > SATURATE ? SATURATE : x);

// A refusal from inside the count, thrown and caught by costOf.
function over(why) {
	const e = new Error('pattern cost: ' + why);
	e.costOver = why;
	return e;
}

function spend(ctx, k) {
	ctx.work += k;
	if (ctx.work > MAX_COUNTING) throw over('work');
}

// ---- code unit sets: sorted, disjoint, inclusive [lo, hi] pairs, one array

const UNIT_MAX = 0xffff;
const byNumber = (x, y) => x - y;

function unitSet(pairs) {
	const list = [];
	for (let k = 0; k < pairs.length; k += 2) list.push([pairs[k], pairs[k + 1]]);
	list.sort(function (a, b) { return a[0] - b[0] || a[1] - b[1]; });
	const out = [];
	for (let k = 0; k < list.length; k++) {
		if (out.length && list[k][0] <= out[out.length - 1] + 1) {
			if (list[k][1] > out[out.length - 1]) out[out.length - 1] = list[k][1];
		} else {
			out.push(list[k][0], list[k][1]);
		}
	}
	return out;
}

function unitComplement(set) {
	const out = [];
	let next = 0;
	for (let k = 0; k < set.length; k += 2) {
		if (set[k] > next) out.push(next, set[k] - 1);
		next = set[k + 1] + 1;
	}
	if (next <= UNIT_MAX) out.push(next, UNIT_MAX);
	return out;
}

function unitHas(set, code) {
	let lo = 0;
	let hi = (set.length >> 1) - 1;
	while (lo <= hi) {
		const mid = (lo + hi) >> 1;
		if (code < set[2 * mid]) hi = mid - 1;
		else if (code > set[(2 * mid) + 1]) lo = mid + 1;
		else return true;
	}
	return false;
}

// The other case of every ASCII letter in the set: exactly what the `i` flag
// adds among ASCII characters (without `u`, JavaScript never folds a
// character past ASCII onto one inside it, or the reverse).
function withAsciiCases(set) {
	const extra = [];
	for (let k = 0; k < set.length; k += 2) {
		const ulo = Math.max(set[k], 0x41);
		const uhi = Math.min(set[k + 1], 0x5a);
		if (ulo <= uhi) extra.push(ulo + 32, uhi + 32);
		const llo = Math.max(set[k], 0x61);
		const lhi = Math.min(set[k + 1], 0x7a);
		if (llo <= lhi) extra.push(llo - 32, lhi - 32);
	}
	return extra.length ? unitSet(set.concat(extra)) : set;
}

const DIGIT_UNITS = [0x30, 0x39];
const WORD_UNITS = [0x30, 0x39, 0x41, 0x5a, 0x5f, 0x5f, 0x61, 0x7a];
const SPACE_UNITS = unitSet([0x09, 0x0d, 0x20, 0x20, 0xa0, 0xa0, 0x1680, 0x1680, 0x2000, 0x200a, 0x2028, 0x2029, 0x202f, 0x202f, 0x205f, 0x205f, 0x3000, 0x3000, 0xfeff, 0xfeff]);
const DOT_UNITS = unitComplement(unitSet([0x0a, 0x0a, 0x0d, 0x0d, 0x2028, 0x2029]));
const ALL_UNITS = [0, UNIT_MAX];

// What a class node may read, never less than the engine does: under `i` a
// class gains the other case of its ASCII letters, and everything past ASCII
// when it holds anything past ASCII; a negated class is the complement of
// what is surely in it (the set and its ASCII cases), so it holds everything
// the engine lets it match.
function readableUnits(node, ci) {
	if (node.negated) return unitComplement(ci ? withAsciiCases(node.units) : node.units);
	if (!ci) return node.units;
	const cased = withAsciiCases(node.units);
	const pastAscii = node.units.length > 0 && node.units[node.units.length - 1] >= 0x80;
	return pastAscii ? unitSet(cased.concat([0x80, UNIT_MAX])) : cased;
}

// ---- the regex as a tree
//
// The source as JavaScript reads it without the `u` flag (Annex B), as nodes:
//   { t: 'units', units, negated }      reads one code unit of the set
//   { t: 'seq', items } / { t: 'alt', items }
//   { t: 'repeat', body, min, max }     max Infinity when open-ended
//   { t: 'group', body, capture }       capture: its number, or 0
//   { t: 'start' } { t: 'end' } { t: 'boundary', negated }
//   { t: 'look', body, behind, negated }
//   { t: 'backref', number } / { t: 'backref', name }
// readRegex throws on anything it does not read; costOf then refuses.

const isHexDigit = c => c !== undefined && /^[0-9a-fA-F]$/.test(c);
const isOctalDigit = c => c !== undefined && c >= '0' && c <= '7';
const isDecimalDigit = c => c !== undefined && c >= '0' && c <= '9';
const QUANTIFIER_BRACE = /^\{([0-9]+)(,([0-9]*))?\}/;
const SIMPLE_ESCAPES = { f: 0x0c, n: 0x0a, r: 0x0d, t: 0x09, v: 0x0b };

// How many capturing groups the source holds, and whether any has a name: a
// `\2` is a backreference only when there are two groups, and `\k` names a
// group only when one has a name.
function countCaptures(src) {
	let count = 0;
	let named = false;
	let inClass = false;
	for (let i = 0; i < src.length; i++) {
		const c = src[i];
		if (c === '\\') { i++; continue; }
		if (inClass) { if (c === ']') inClass = false; continue; }
		if (c === '[') { inClass = true; continue; }
		if (c !== '(') continue;
		if (src[i + 1] !== '?') count++;
		else if (src[i + 2] === '<' && src[i + 3] !== '=' && src[i + 3] !== '!') { count++; named = true; }
	}
	return { count: count, named: named };
}

// A group's name as the engine reads it: `\u0061` and `\u{61}` in a name are
// the letter they spell, so `(?<\u0061>x)\k<a>` names one group.
function groupName(raw) {
	return raw.replace(/\\u\{([0-9a-fA-F]{1,6})\}|\\u([0-9a-fA-F]{4})/g, function (all, braced, four) {
		const code = parseInt(braced || four, 16);
		return code > 0x10ffff ? all : String.fromCodePoint(code);
	});
}

function readRegex(source, flags) {
	const src = String(source);
	const n = src.length;
	const captures = countCaptures(src);
	const names = {};
	let i = 0;
	let captureNumber = 0;

	const single = code => ({ t: 'units', units: [code, code], negated: false });

	function quantifier() {
		const c = src[i];
		if (c === '*' || c === '+' || c === '?') {
			i++;
			if (src[i] === '?') i++;
			if (c === '*') return { min: 0, max: Infinity };
			return c === '+' ? { min: 1, max: Infinity } : { min: 0, max: 1 };
		}
		if (c !== '{') return null;
		// Read against the whole rest of the source: the engine sets no limit
		// on a count's digits, and a count read as text here while the engine
		// repeats by it would be a cheap tree counted for a costly one.
		const m = QUANTIFIER_BRACE.exec(src.slice(i));
		if (!m) return null;
		i += m[0].length;
		if (src[i] === '?') i++;
		const min = Number(m[1]);
		let max = min;
		if (m[2] !== undefined) max = m[3] === '' ? Infinity : Number(m[3]);
		return { min: min, max: max };
	}
	const quantifierNext = () => src[i] === '*' || src[i] === '+' || src[i] === '?' || (src[i] === '{' && QUANTIFIER_BRACE.test(src.slice(i)));

	// A legacy octal escape, \0 to \377, as V8 reads it; i is on its first digit.
	function octal() {
		let value = src.charCodeAt(i++) - 48;
		if (isOctalDigit(src[i])) {
			value = (value * 8) + (src.charCodeAt(i++) - 48);
			if (value < 32 && isOctalDigit(src[i])) value = (value * 8) + (src.charCodeAt(i++) - 48);
		}
		return value;
	}

	// \f \n \r \t \v \xHH \uHHHH \cX, outside a class or in one; i is on the
	// backslash. A code unit, or null for none of these.
	function characterEscape(inClass) {
		const c = src[i + 1];
		if (Object.prototype.hasOwnProperty.call(SIMPLE_ESCAPES, c)) { i += 2; return SIMPLE_ESCAPES[c]; }
		if (c === 'x') {
			if (isHexDigit(src[i + 2]) && isHexDigit(src[i + 3])) { i += 4; return parseInt(src.slice(i - 2, i), 16); }
			i += 2;
			return 0x78;
		}
		if (c === 'u') {
			if (isHexDigit(src[i + 2]) && isHexDigit(src[i + 3]) && isHexDigit(src[i + 4]) && isHexDigit(src[i + 5])) { i += 6; return parseInt(src.slice(i - 4, i), 16); }
			i += 2;
			return 0x75;
		}
		if (c === 'c') {
			const letter = src[i + 2];
			if (letter !== undefined && (/^[A-Za-z]$/.test(letter) || (inClass && /^[0-9_]$/.test(letter)))) {
				i += 3;
				return letter.charCodeAt(0) % 32;
			}
			// A backslash that starts nothing is itself; the c is read next.
			i += 1;
			return 0x5c;
		}
		return null;
	}

	function escapeClass(c) {
		if (c === 'd') return DIGIT_UNITS;
		if (c === 'D') return unitComplement(DIGIT_UNITS);
		if (c === 'w') return WORD_UNITS;
		if (c === 'W') return unitComplement(WORD_UNITS);
		if (c === 's') return SPACE_UNITS;
		if (c === 'S') return unitComplement(SPACE_UNITS);
		return null;
	}

	// One member of a class: { code } or { units } (a class escape).
	function classAtom() {
		if (src[i] !== '\\') return { code: src.charCodeAt(i++) };
		const c = src[i + 1];
		if (c === undefined) throw new Error('trailing backslash');
		const units = escapeClass(c);
		if (units) { i += 2; return { units: units }; }
		if (c === 'b') { i += 2; return { code: 0x08 }; }
		if (isDecimalDigit(c)) {
			i++;
			if (c === '8' || c === '9') { i++; return { code: c.charCodeAt(0) }; }
			return { code: octal() };
		}
		const code = characterEscape(true);
		if (code !== null) return { code: code };
		i += 2;
		return { code: c.charCodeAt(0) };
	}
	function pushAtom(parts, atom) {
		if (atom.units) { for (let k = 0; k < atom.units.length; k++) parts.push(atom.units[k]); } else { parts.push(atom.code, atom.code); }
	}

	function readClass() {
		i++;
		let negated = false;
		if (src[i] === '^') { negated = true; i++; }
		const parts = [];
		while (i < n && src[i] !== ']') {
			const from = classAtom();
			if (src[i] === '-' && i + 1 < n && src[i + 1] !== ']') {
				i++;
				const to = classAtom();
				if (from.code !== undefined && to.code !== undefined) {
					if (from.code > to.code) throw new Error('range out of order');
					parts.push(from.code, to.code);
				} else {
					// A class escape at either end makes the dash a dash (Annex B).
					pushAtom(parts, from);
					parts.push(0x2d, 0x2d);
					pushAtom(parts, to);
				}
			} else {
				pushAtom(parts, from);
			}
		}
		if (i >= n) throw new Error('unterminated class');
		i++;
		return { t: 'units', units: unitSet(parts), negated: negated };
	}

	// An escape outside a class (not \b or \B); i is on the backslash.
	function atomEscape() {
		const c = src[i + 1];
		if (c === undefined) throw new Error('trailing backslash');
		const units = escapeClass(c);
		if (units) { i += 2; return { t: 'units', units: units, negated: false }; }
		if (c >= '1' && c <= '9') {
			let j = i + 1;
			let value = 0;
			while (isDecimalDigit(src[j]) && value <= captures.count) value = (value * 10) + (src.charCodeAt(j++) - 48);
			if (value <= captures.count && !isDecimalDigit(src[j])) { i = j; return { t: 'backref', number: value }; }
			// Past the groups there are: a legacy octal escape, or the digit itself.
			i++;
			if (c === '8' || c === '9') { i++; return single(c.charCodeAt(0)); }
			return single(octal());
		}
		if (c === '0') { i++; return single(octal()); }
		if (c === 'k' && captures.named) {
			const close = src.indexOf('>', i + 3);
			if (src[i + 2] !== '<' || close === -1) throw new Error('bad group reference');
			const name = groupName(src.slice(i + 3, close));
			i = close + 1;
			return { t: 'backref', name: name };
		}
		const code = characterEscape(false);
		if (code !== null) return single(code);
		i += 2;
		return single(c.charCodeAt(0));
	}

	function readGroup() {
		i++;
		let node;
		if (src[i] !== '?') {
			node = { t: 'group', body: null, capture: ++captureNumber };
		} else if (src[i + 1] === ':') {
			i += 2;
			node = { t: 'group', body: null, capture: 0 };
		} else if (src[i + 1] === '=' || src[i + 1] === '!') {
			node = { t: 'look', body: null, behind: false, negated: src[i + 1] === '!' };
			i += 2;
		} else if (src[i + 1] === '<' && (src[i + 2] === '=' || src[i + 2] === '!')) {
			node = { t: 'look', body: null, behind: true, negated: src[i + 2] === '!' };
			i += 3;
		} else if (src[i + 1] === '<') {
			const close = src.indexOf('>', i + 2);
			if (close === -1) throw new Error('bad group name');
			node = { t: 'group', body: null, capture: ++captureNumber };
			names[groupName(src.slice(i + 2, close))] = node.capture;
			i = close + 1;
		} else {
			// A modifier group, (?i:…), or anything newer: not read, so refused.
			throw new Error('unknown group');
		}
		node.body = alternatives();
		if (src[i] !== ')') throw new Error('unterminated group');
		i++;
		return node;
	}

	function term() {
		const c = src[i];
		let node;
		if (c === '^') { i++; node = { t: 'start' }; }
		else if (c === '$') { i++; node = { t: 'end' }; }
		else if (c === '\\' && (src[i + 1] === 'b' || src[i + 1] === 'B')) { node = { t: 'boundary', negated: src[i + 1] === 'B' }; i += 2; }
		else if (c === '\\') node = atomEscape();
		else if (c === '(') node = readGroup();
		else if (c === '.') { i++; node = { t: 'units', units: DOT_UNITS, negated: false }; }
		else if (c === '[') node = readClass();
		else if (quantifierNext()) throw new Error('nothing to repeat');
		else node = single(src.charCodeAt(i++));
		const q = quantifier();
		if (!q) return node;
		if (node.t === 'start' || node.t === 'end' || node.t === 'boundary' || (node.t === 'look' && node.behind)) throw new Error('nothing to repeat');
		if (q.max < q.min) throw new Error('numbers out of order');
		if (quantifierNext()) throw new Error('nothing to repeat');
		return { t: 'repeat', body: node, min: q.min, max: q.max };
	}

	function sequence() {
		const items = [];
		while (i < n && src[i] !== '|' && src[i] !== ')') items.push(term());
		return items.length === 1 ? items[0] : { t: 'seq', items: items };
	}

	function alternatives() {
		const items = [sequence()];
		while (src[i] === '|') { i++; items.push(sequence()); }
		return items.length === 1 ? items[0] : { t: 'alt', items: items };
	}

	const tree = alternatives();
	if (i !== n) throw new Error('unbalanced )');
	return { tree: tree, ci: String(flags || '').indexOf('i') !== -1, captures: captures.count, names: names };
}

// ---- the counted position automaton

function canReadNothing(node) {
	switch (node.t) {
	case 'units': return false;
	case 'seq': return node.items.every(canReadNothing);
	case 'alt': return node.items.some(canReadNothing);
	case 'repeat': return node.min === 0 || canReadNothing(node.body);
	case 'group': return canReadNothing(node.body);
	default: return true;
	}
}

// How many capture groups a node holds: each one the engine clears when a
// repeat around it starts another pass.
function capturesIn(node) {
	let count = node.t === 'group' && node.capture ? 1 : 0;
	if (node.body) count += capturesIn(node.body);
	if (node.items) for (let k = 0; k < node.items.length; k++) count += capturesIn(node.items[k]);
	return count;
}

// The body of a lookbehind, which the engine matches from right to left.
function backwards(node) {
	switch (node.t) {
	case 'seq': return { t: 'seq', items: node.items.slice().reverse().map(backwards) };
	case 'alt': return { t: 'alt', items: node.items.map(backwards) };
	case 'repeat': return { t: 'repeat', body: backwards(node.body), min: node.min, max: node.max };
	case 'group': return { t: 'group', body: backwards(node.body), capture: node.capture };
	default: return node;
	}
}

// Every count below is a pair [routes, boundaries]: how many routes, and how
// many capture group boundaries they cross between them. Two routes in a row
// are one route that crosses the boundaries of both.
const ONE_ROUTE = [1, 0];
const NO_ROUTE = [0, 0];
const join = (x, y) => [saturated(x[0] * y[0]), saturated(saturated(x[0] * y[1]) + saturated(x[1] * y[0]))];
const both = (x, y) => [saturated(x[0] + y[0]), saturated(x[1] + y[1])];
const crossing = (x, k) => [x[0], saturated(x[1] + saturated(x[0] * k))];

// automaton(tree, ctx) -> { kinds, routeTo, routeBy, out }
//   kinds[p]    the set position p reads (an index into ctx.sets); -1 for the
//               start and for dead positions
//   routeTo/By  the positions reachable from p without reading, and how many
//               routes lead to each (dead positions left out: they only cost)
//   out[p]      what one visit of p costs, in steps: every route that leaves
//               it (to a position, to a dead one, to the end of the pattern),
//               plus CAPTURE_STEP for each capture boundary each one crosses
//               (a group entered or left, or cleared as a repeat around it
//               starts another pass)
function automaton(tree, ctx) {
	const kinds = [-1];
	const follow = [new Map()];
	let routes = 0;

	function position(kind) {
		if (kinds.length >= MAX_POSITIONS) throw over('size');
		kinds.push(kind);
		follow.push(new Map());
		return kinds.length - 1;
	}
	// The set a class node reads, interned: every copy of the node shares it.
	function kindOf(node) {
		let kind = ctx.nodeKinds.get(node);
		if (kind !== undefined) return kind;
		const units = readableUnits(node, ctx.ci);
		spend(ctx, units.length + 1);
		const key = units.join(',');
		kind = ctx.setIndex.get(key);
		if (kind === undefined) {
			kind = ctx.sets.length;
			ctx.sets.push(units);
			ctx.setIndex.set(key, kind);
		}
		ctx.nodeKinds.set(node, kind);
		return kind;
	}
	function add(into, from, times) {
		if (!times[0]) return;
		spend(ctx, from.size + 1);
		from.forEach(function (count, p) {
			const had = into.get(p);
			const more = join(times, count);
			into.set(p, had ? both(had, more) : more);
		});
	}
	function cross(map, k) {
		if (!k) return map;
		spend(ctx, map.size + 1);
		const out = new Map();
		map.forEach(function (count, p) { out.set(p, crossing(count, k)); });
		return out;
	}
	function link(last, first) {
		spend(ctx, (last.size * first.size) + 1);
		last.forEach(function (c, p) {
			const row = follow[p];
			first.forEach(function (d, q) {
				const had = row.get(q);
				if (had === undefined && ++routes > MAX_ROUTES) throw over('size');
				const more = join(c, d);
				row.set(q, had ? both(had, more) : more);
			});
		});
	}
	// A fragment: { empty, first, last }: the ways it matches nothing, and the
	// routes reaching each position it may read first, or leaving each one it
	// may read last.
	const nothing = () => ({ empty: ONE_ROUTE, first: new Map(), last: new Map() });
	function then(a, b) {
		link(a.last, b.first);
		spend(ctx, a.first.size + b.last.size + 1);
		const first = new Map(a.first);
		add(first, b.first, a.empty);
		const last = new Map(b.last);
		add(last, a.last, b.empty);
		return { empty: join(a.empty, b.empty), first: first, last: last };
	}
	// One pass of a repeat starts by clearing the captures in its body.
	function passStart(f, captures) {
		if (!captures) return f;
		return { empty: crossing(f.empty, captures), first: cross(f.first, captures), last: f.last };
	}
	// A pass the repeat may skip: the engine refuses one that read nothing, so
	// each way the body matches nothing is a route that dies.
	function optionalPass(f) {
		if (!f.empty[0]) return f;
		const first = new Map(f.first);
		first.set(position(-1), f.empty);
		return { empty: NO_ROUTE, first: first, last: f.last };
	}
	function build(node) {
		switch (node.t) {
		case 'units': {
			const p = position(kindOf(node));
			return { empty: NO_ROUTE, first: new Map([[p, ONE_ROUTE]]), last: new Map([[p, ONE_ROUTE]]) };
		}
		case 'seq': {
			let f = nothing();
			for (let k = 0; k < node.items.length; k++) f = then(f, build(node.items[k]));
			return f;
		}
		case 'alt': {
			const f = { empty: NO_ROUTE, first: new Map(), last: new Map() };
			for (let k = 0; k < node.items.length; k++) {
				const b = build(node.items[k]);
				f.empty = both(f.empty, b.empty);
				add(f.first, b.first, ONE_ROUTE);
				add(f.last, b.last, ONE_ROUTE);
			}
			return f;
		}
		case 'group': {
			const f = build(node.body);
			if (!node.capture) return f;
			// Entering the group is one boundary, and leaving it another.
			return { empty: crossing(f.empty, 2), first: cross(f.first, 1), last: cross(f.last, 1) };
		}
		case 'backref':
			return build({ t: 'repeat', body: { t: 'units', units: ALL_UNITS, negated: false }, min: 0, max: Infinity });
		case 'look': {
			let cost = ctx.lookCosts.get(node);
			if (cost === undefined) {
				cost = attemptsOf(node.behind ? backwards(node.body) : node.body, ctx);
				ctx.lookCosts.set(node, cost);
			}
			return { empty: ONE_ROUTE, first: new Map([[position(-1), [cost, 0]]]), last: new Map() };
		}
		case 'repeat': {
			const captures = capturesIn(node.body);
			let copies = node.min;
			if (copies > COST_UNITS + 1 && !canReadNothing(node.body)) copies = COST_UNITS + 1;
			let f = nothing();
			for (let k = 0; k < copies; k++) f = then(f, passStart(build(node.body), captures));
			if (node.max === Infinity) {
				const p = optionalPass(passStart(build(node.body), captures));
				link(p.last, p.first);
				return then(f, { empty: ONE_ROUTE, first: p.first, last: p.last });
			}
			// The optional passes nest, (B(B(B)?)?)?, so each may end the repeat:
			// built from the innermost out, the ends gathered in place.
			const optional = Math.min(node.max - node.min, COST_UNITS);
			const passes = [];
			for (let k = 0; k < optional; k++) passes.push(optionalPass(passStart(build(node.body), captures)));
			const tail = nothing();
			for (let k = optional - 1; k >= 0; k--) {
				link(passes[k].last, tail.first);
				add(tail.last, passes[k].last, ONE_ROUTE);
				tail.first = passes[k].first;
			}
			return then(f, tail);
		}
		default:
			// ^, $, \b, \B: read as always passing, which only adds routes.
			return nothing();
		}
	}

	const whole = build(tree);
	const stepsOf = count => saturated(count[0] + saturated(count[1] * ctx.captureStep));
	const P = kinds.length;
	const routeTo = new Array(P);
	const routeBy = new Array(P);
	const out = new Float64Array(P);
	whole.first.forEach(function (c, q) { follow[0].set(q, c); });
	for (let p = 0; p < P; p++) {
		const to = [];
		const by = [];
		let total = 0;
		if (p === 0) total = stepsOf(whole.empty);
		else if (whole.last.has(p)) total = stepsOf(whole.last.get(p));
		spend(ctx, follow[p].size + 1);
		for (const entry of follow[p]) {
			total = saturated(total + stepsOf(entry[1]));
			if (kinds[entry[0]] !== -1) { to.push(entry[0]); by.push(entry[1][0]); }
		}
		routeTo[p] = to;
		routeBy[p] = by;
		out[p] = total;
	}
	return { kinds: kinds, routeTo: routeTo, routeBy: routeBy, out: out };
}

// ---- the search over every value

// The code units split into the kinds no set tells apart, each inside one of
// three ranges: not a surrogate, a high surrogate, a low surrogate. Answers
// { range: ['unit' | 'high' | 'low'], ofSet: [set index -> kind indexes] }.
function unitKinds(ctx) {
	const sets = ctx.sets;
	const cuts = [0, 0xd800, 0xdc00, 0xe000, UNIT_MAX + 1];
	for (let s = 0; s < sets.length; s++) {
		for (let k = 0; k < sets[s].length; k += 2) cuts.push(sets[s][k], sets[s][k + 1] + 1);
	}
	spend(ctx, cuts.length * 2);
	cuts.sort(byNumber);
	const seen = new Map();
	const range = [];
	const ofSet = [];
	for (let s = 0; s < sets.length; s++) ofSet.push([]);
	for (let k = 0; k + 1 < cuts.length; k++) {
		const lo = cuts[k];
		if (lo === cuts[k + 1] || lo > UNIT_MAX) continue;
		spend(ctx, sets.length + 1);
		const members = [];
		for (let s = 0; s < sets.length; s++) if (unitHas(sets[s], lo)) members.push(s);
		if (!members.length) continue;
		let kind = 'unit';
		if (lo >= 0xd800 && lo <= 0xdbff) kind = 'high';
		else if (lo >= 0xdc00 && lo <= 0xdfff) kind = 'low';
		const key = kind + ':' + members.join(',');
		if (seen.has(key)) continue;
		seen.set(key, range.length);
		for (let m = 0; m < members.length; m++) ofSet[members[m]].push(range.length);
		range.push(kind);
	}
	return { range: range, ofSet: ofSet };
}

// attemptsOf(tree, ctx) -> number
//
// The most steps the engine can take matching `tree` from the start of any
// value of up to ctx.codePoints code points (thrown `over` past ctx.limit).
// Every inner step of the search itself is charged to ctx.work.
function attemptsOf(tree, ctx) {
	const a = automaton(tree, ctx);
	const split = unitKinds(ctx);
	const P = a.kinds.length;
	const K = split.range.length;
	const kindsOf = [];
	for (let p = 0; p < P; p++) kindsOf.push(a.kinds[p] === -1 ? [] : split.ofSet[a.kinds[p]]);
	const isHigh = new Uint8Array(K);
	const isLow = new Uint8Array(K);
	for (let k = 0; k < K; k++) {
		if (split.range[k] === 'high') isHigh[k] = 1;
		if (split.range[k] === 'low') isLow[k] = 1;
	}

	// Scratch, reused: the routes reaching each position, one buffer per kind
	// of code unit read (made on first use), and the positions touched.
	const buffers = new Array(K);
	const touched = new Array(K);
	const mergeBuffer = new Float64Array(P);

	// A state: its live positions `at` (ascending), the routes reaching each,
	// and `id`, a number that depends only on which positions are live. Two
	// states that share an id by chance are merged, which can only add.
	function stateFrom(buffer, list) {
		list.sort(byNumber);
		spend(ctx, (list.length * 3) + 1);
		const at = new Int32Array(list.length);
		const routes = new Float64Array(list.length);
		let id = list.length;
		for (let k = 0; k < list.length; k++) {
			at[k] = list[k];
			routes[k] = buffer[list[k]];
			buffer[list[k]] = 0;
			id = Math.imul(id ^ list[k], 0x01000193) >>> 0;
		}
		return { at: at, routes: routes, id: id };
	}
	// Every state one more code unit reaches, with the kind of unit read
	// (low surrogates only when `lowOnly`), pushed onto `into`.
	function successors(state, lowOnly, into) {
		const used = [];
		for (let k = 0; k < state.at.length; k++) {
			const p = state.at[k];
			const routes = state.routes[k];
			const to = a.routeTo[p];
			const by = a.routeBy[p];
			for (let j = 0; j < to.length; j++) {
				const q = to[j];
				const reached = saturated(routes * by[j]);
				const kinds = kindsOf[q];
				ctx.work += kinds.length + 1;
				for (let m = 0; m < kinds.length; m++) {
					const kind = kinds[m];
					if (lowOnly && !isLow[kind]) continue;
					let buffer = buffers[kind];
					if (!buffer) {
						buffer = new Float64Array(P);
						buffers[kind] = buffer;
						touched[kind] = [];
					}
					if (buffer[q] === 0) {
						if (!touched[kind].length) used.push(kind);
						touched[kind].push(q);
					}
					buffer[q] = saturated(buffer[q] + reached);
				}
			}
			if (ctx.work > MAX_COUNTING) throw over('work');
		}
		spend(ctx, used.length + 1);
		used.sort(byNumber);
		for (let u = 0; u < used.length; u++) {
			into.push({ kind: used[u], state: stateFrom(buffers[used[u]], touched[used[u]]) });
			touched[used[u]] = [];
		}
	}
	function cost(state) {
		spend(ctx, state.at.length + 1);
		let sum = 0;
		for (let k = 0; k < state.at.length; k++) sum += state.routes[k] * a.out[state.at[k]];
		return saturated(sum);
	}
	function dearest(list) {
		let most = 0;
		for (let k = 0; k < list.length; k++) {
			const c = cost(list[k]);
			if (c > most) most = c;
		}
		return most;
	}
	// The most routes per position over a group of states.
	function merge(members) {
		if (members.length === 1) return members[0];
		const list = [];
		for (let k = 0; k < members.length; k++) {
			const s = members[k];
			spend(ctx, s.at.length + 1);
			for (let j = 0; j < s.at.length; j++) {
				const q = s.at[j];
				if (mergeBuffer[q] === 0) list.push(q);
				if (s.routes[j] > mergeBuffer[q]) mergeBuffer[q] = s.routes[j];
			}
		}
		return stateFrom(mergeBuffer, list);
	}
	function before(x, y) {
		const shorter = Math.min(x.at.length, y.at.length);
		for (let k = 0; k < shorter; k++) if (x.at[k] !== y.at[k]) return x.at[k] - y.at[k];
		if (x.at.length !== y.at.length) return x.at.length - y.at.length;
		for (let k = 0; k < x.routes.length; k++) if (x.routes[k] !== y.routes[k]) return x.routes[k] - y.routes[k];
		return 0;
	}
	function groupedBy(list, keyOf) {
		const groups = new Map();
		for (let k = 0; k < list.length; k++) {
			const key = keyOf(list[k]);
			const group = groups.get(key);
			if (group) group.push(list[k]); else groups.set(key, [list[k]]);
		}
		const merged = [];
		groups.forEach(function (members) { merged.push(merge(members)); });
		return merged;
	}
	// One state per set of live positions; past `cap`, merged by their first
	// position, then neighbours in order, until few enough.
	function gather(list, cap) {
		let merged = groupedBy(list, function (s) { return s.id; });
		if (merged.length > cap) merged = groupedBy(merged, function (s) { return s.at.length ? s.at[0] : -1; });
		spend(ctx, (merged.length * 8) + 1);
		merged.sort(before);
		if (merged.length > cap) {
			const run = Math.ceil(merged.length / cap);
			const runs = [];
			for (let k = 0; k < merged.length; k += run) runs.push(merge(merged.slice(k, k + run)));
			merged = runs.sort(before);
		}
		return merged;
	}

	function levelKey(list) {
		let h = list.length;
		for (let k = 0; k < list.length; k++) {
			const s = list[k];
			spend(ctx, s.at.length + 1);
			for (let j = 0; j < s.at.length; j++) h = (Math.imul(h ^ s.at[j], 0x01000193) + (s.routes[j] % 1048573)) >>> 0;
			h = Math.imul(h ^ 0x5bd1e995, 0x01000193) >>> 0;
		}
		return h;
	}
	function sameStates(x, y) {
		if (x.length !== y.length) return false;
		for (let k = 0; k < x.length; k++) if (before(x[k], y[k]) !== 0) return false;
		return true;
	}

	// One walk over every value, `levels` long, keeping at most `cap` states a
	// level. By code unit (`pairs` false), each level reads one code unit; by
	// code point, a high surrogate followed by a low one is read in one level,
	// two code units for one code point.
	function walk(levels, pairs, cap) {
		// Lengths by a number that depends only on their states; a match is
		// then checked state by state, so a shared number never repeats the
		// wrong one.
		const lengthsByKey = new Map();
		const statesAt = [];
		const added = [];
		let states = [{ at: Int32Array.of(0), routes: Float64Array.of(1), id: 0 }];
		let total = a.out[0];
		for (let level = 0; level < levels && states.length; level++) {
			const next = [];
			const middle = [];
			for (let k = 0; k < states.length; k++) {
				const reached = [];
				successors(states[k], false, reached);
				for (let r = 0; r < reached.length; r++) {
					next.push(reached[r].state);
					if (pairs && isHigh[reached[r].kind]) {
						middle.push(reached[r].state);
						const second = [];
						successors(reached[r].state, true, second);
						for (let l = 0; l < second.length; l++) next.push(second[l].state);
					}
				}
			}
			states = gather(next, cap);
			const step = saturated(dearest(middle) + dearest(states));
			total = saturated(total + step);
			if (total > ctx.limit) throw over('steps');
			added.push(step);
			const key = levelKey(states);
			const earlier = lengthsByKey.get(key) || [];
			let from = -1;
			for (let e = 0; e < earlier.length && from < 0; e++) if (sameStates(statesAt[earlier[e]], states)) from = earlier[e];
			if (from >= 0) {
				// The same states as an earlier length: from here every length
				// repeats the lengths that followed that one.
				const period = level - from;
				for (let rest = level + 1; rest < levels; rest++) {
					total = saturated(total + added[from + 1 + ((rest - from - 1) % period)]);
					if (total > ctx.limit) throw over('steps');
				}
				break;
			}
			earlier.push(level);
			lengthsByKey.set(key, earlier);
			statesAt.push(states);
		}
		return total;
	}

	// The walks, cheapest first; each one is a bound, so the first that stays
	// within the line decides, and only a walk past the line tries the next.
	// A pattern that never reads a surrogate meets values of at most
	// ctx.codePoints code units, so one walk by code unit is exact about
	// length. One that may read them meets up to twice as many code units, a
	// pair being one code point: walked by code unit to twice the length first
	// (a bound: it lets a value hold more code points than it can), then by
	// code point, which is exact about pairs. Each walk keeps MAX_LEVEL_SETS
	// states a level, then WIDE_LEVEL_SETS: merging is cheap but adds, and a
	// pattern whose values line up many ways (a run of spaces, then a counted
	// group) needs the wider walk to be counted close to what it costs.
	let readsSurrogates = false;
	for (let p = 0; p < P && !readsSurrogates; p++) {
		for (let m = 0; m < kindsOf[p].length; m++) if (isHigh[kindsOf[p][m]] || isLow[kindsOf[p][m]]) readsSurrogates = true;
	}
	const walks = [];
	if (!readsSurrogates) walks.push([ctx.codePoints, false]);
	else {
		if (ctx.walk !== 'pairs') walks.push([2 * ctx.codePoints, false]);
		if (ctx.walk !== 'units') walks.push([ctx.codePoints, true]);
	}
	const caps = ctx.levelSets ? [ctx.levelSets] : [MAX_LEVEL_SETS, WIDE_LEVEL_SETS];
	for (let w = 0; w < walks.length; w++) {
		for (let c = 0; c < caps.length; c++) {
			try {
				return walk(walks[w][0], walks[w][1], caps[c]);
			} catch (e) {
				if (!e || e.costOver !== 'steps') throw e;
			}
		}
	}
	throw over('steps');
}

function countingContext(read, options) {
	const o = options || {};
	return {
		ci: read.ci,
		sets: [],
		setIndex: new Map(),
		nodeKinds: new Map(),
		lookCosts: new Map(),
		work: 0,
		limit: o.limit === undefined ? MAX_STEPS : o.limit,
		codePoints: o.codePoints || COST_CODE_POINTS,
		// Tests: one cap on the states a level, instead of the two in turn.
		levelSets: o.levelSets || null,
		captureStep: o.captureStep === undefined ? CAPTURE_STEP : o.captureStep,
		// Tests: one walk alone, 'units' or 'pairs'.
		walk: o.walk || null,
	};
}

// Every cost asked for, by flags and regex: authoring asks once per element
// per save, the element editor once per keystroke. Bounded like COMPILED.
const COSTS = new Map();
const MAX_COSTS = 500;

/**
 * costOf(regex, flags) -> { steps, over }
 *
 * The bound on the steps the engine can take checking the implicitly
 * anchored regex against any short text value: `steps` a whole number when
 * it is within MAX_STEPS (and `over` null), else `steps` null and `over` why:
 * 'steps' (past the line), 'size' (too large to count), 'work' (too costly to
 * count) or 'syntax' (not read). Call it on a regex that compiles.
 */
function costOf(regex, flags) {
	const key = String(flags || '') + '/' + String(regex);
	if (COSTS.has(key)) return COSTS.get(key);
	let result;
	try {
		const read = readRegex(regex, flags);
		const steps = Math.ceil(attemptsOf(read.tree, countingContext(read)));
		result = steps > MAX_STEPS ? { steps: null, over: 'steps' } : { steps: steps, over: null };
	} catch (e) {
		result = { steps: null, over: e && e.costOver ? e.costOver : 'syntax' };
	}
	if (COSTS.size >= MAX_COSTS) COSTS.clear();
	COSTS.set(key, result);
	return result;
}

/** checkCost(regex, flags) -> null | 'slow': the bound, as a refusal. */
function checkCost(regex, flags) {
	return costOf(regex, flags).over ? 'slow' : null;
}

// For the tests that hold the bound against an independent matcher: the
// tree, the steps the automaton gives ONE value (no search), and the search
// with its limits changed (a shorter ceiling, fewer sets, no capture weight).
const costInternals = {
	readRegex: readRegex,
	stepsForValue: function (regex, flags, value, options) {
		const read = readRegex(regex, flags);
		const o = options || {};
		const ctx = countingContext(read, { limit: Infinity, codePoints: o.codePoints, captureStep: o.captureStep, walk: o.walk });
		const a = automaton(read.tree, ctx);
		let live = new Map([[0, 1]]);
		let total = a.out[0];
		for (let k = 0; k < value.length && live.size; k++) {
			const code = value.charCodeAt(k);
			const next = new Map();
			for (const entry of live) {
				const to = a.routeTo[entry[0]];
				for (let j = 0; j < to.length; j++) {
					if (unitHas(ctx.sets[a.kinds[to[j]]], code)) next.set(to[j], (next.get(to[j]) || 0) + (entry[1] * a.routeBy[entry[0]][j]));
				}
			}
			live = next;
			for (const entry of live) total += entry[1] * a.out[entry[0]];
		}
		return total;
	},
	stepsWithin: function (regex, flags, options) {
		const read = readRegex(regex, flags);
		const o = options || {};
		return attemptsOf(read.tree, countingContext(read, { limit: Infinity, codePoints: o.codePoints, levelSets: o.levelSets, captureStep: o.captureStep, walk: o.walk }));
	},
	// The check's own counted work on a regex, under the real limits.
	workOf: function (regex, flags) {
		const read = readRegex(regex, flags);
		const ctx = countingContext(read);
		let over = null;
		try { attemptsOf(read.tree, ctx); } catch (e) { if (!e || !e.costOver) throw e; over = e.costOver; }
		return { work: ctx.work, over: over };
	},
	limits: { MAX_POSITIONS: MAX_POSITIONS, MAX_ROUTES: MAX_ROUTES, MAX_LEVEL_SETS: MAX_LEVEL_SETS, WIDE_LEVEL_SETS: WIDE_LEVEL_SETS, MAX_COUNTING: MAX_COUNTING, COST_CODE_POINTS: COST_CODE_POINTS },
	forget: function () { COSTS.clear(); },
};

// --------------------------------------------------------- the option's shape

// Why a pattern option was refused, as tokens both repos put words to.
//   shape       not { preset } or { regex, flags?, message }
//   preset      a preset id keepr does not know
//   both        a preset AND a regex
//   regex       an empty or non-text regex
//   too_long    a regex over 200 characters
//   flags       a flag other than 'i'
//   compile     a regex JavaScript does not accept
//   unsafe      checkSafety refused it
//   slow        checkCost refused it: past MAX_STEPS, or too large or too
//               costly to count
//   message     a missing, empty or over-long sentence
const PATTERN_KEYS = ['preset', 'regex', 'flags', 'message'];

/**
 * checkPattern(pattern) -> { ok: true, resolved } | { ok: false, reason }
 *
 * The whole authoring rule for `options.pattern`, the same in the element
 * editor and the API. `resolved` is { regex, flags, message, preset } — what
 * a value is checked against.
 */
function checkPattern(pattern) {
	if (!isPlainObject(pattern)) return { ok: false, reason: 'shape' };
	const keys = Object.keys(pattern);
	for (let k = 0; k < keys.length; k++) {
		if (PATTERN_KEYS.indexOf(keys[k]) === -1) return { ok: false, reason: 'shape' };
	}
	const hasPreset = pattern.preset !== undefined && pattern.preset !== null;
	const hasRegex = pattern.regex !== undefined && pattern.regex !== null;
	if (hasPreset && hasRegex) return { ok: false, reason: 'both' };
	if (pattern.message !== undefined && pattern.message !== null) {
		if (typeof pattern.message !== 'string' || !pattern.message.trim() || countChars(pattern.message) > MAX_MESSAGE) {
			return { ok: false, reason: 'message' };
		}
	}
	if (hasPreset) {
		const preset = presetOf(pattern.preset);
		if (!preset) return { ok: false, reason: 'preset' };
		if (pattern.flags !== undefined && pattern.flags !== null && pattern.flags !== '') return { ok: false, reason: 'flags' };
		return { ok: true, resolved: resolvePattern(pattern) };
	}
	if (!hasRegex) return { ok: false, reason: 'shape' };
	if (typeof pattern.regex !== 'string' || !pattern.regex) return { ok: false, reason: 'regex' };
	if (countChars(pattern.regex) > MAX_REGEX) return { ok: false, reason: 'too_long' };
	const flags = pattern.flags === undefined || pattern.flags === null ? '' : pattern.flags;
	if (typeof flags !== 'string' || (flags !== '' && ALLOWED_FLAGS.indexOf(flags) === -1)) return { ok: false, reason: 'flags' };
	if (typeof pattern.message !== 'string') return { ok: false, reason: 'message' };
	// Both spellings must compile: the regex alone (so "a)|(b" cannot close
	// the anchoring group and open one of its own) and anchored.
	try {
		new RegExp(pattern.regex, flags); // eslint-disable-line no-new
		new RegExp(anchored(pattern.regex), flags); // eslint-disable-line no-new
	} catch (e) {
		return { ok: false, reason: 'compile' };
	}
	if (checkSafety(pattern.regex)) return { ok: false, reason: 'unsafe' };
	if (checkCost(pattern.regex, flags)) return { ok: false, reason: 'slow' };
	return { ok: true, resolved: resolvePattern(pattern) };
}

/**
 * resolvePattern(pattern) -> { regex, flags, message, preset } | null
 *
 * What a value is checked against. A preset brings its regex and sentence
 * (the author's `message` replaces the sentence); a custom pattern is itself.
 * Lenient, for the READ side: a flattened schema element's resolved pattern
 * (which carries both `preset` and `regex`) resolves through its preset, and
 * anything without a usable regex resolves to null — nothing to enforce.
 */
function resolvePattern(pattern) {
	if (!isPlainObject(pattern)) return null;
	const own = typeof pattern.message === 'string' && pattern.message.trim() ? pattern.message : null;
	const preset = presetOf(pattern.preset);
	if (preset) return { regex: preset.regex, flags: '', message: own || preset.message, preset: preset.id };
	if (typeof pattern.regex !== 'string' || !pattern.regex) return null;
	const flags = pattern.flags === 'i' ? 'i' : '';
	return { regex: pattern.regex, flags: flags, message: own || 'This value does not have the expected format.', preset: null };
}

// One compiled RegExp per (flags, regex): a card's pattern is compiled once,
// not once per value. Bounded, because custom patterns are author content.
const COMPILED = new Map();
const MAX_COMPILED = 500;

/** The anchored RegExp for a resolved pattern, or null when it does not compile. */
function compiled(resolved) {
	if (!resolved) return null;
	const key = resolved.flags + '/' + resolved.regex;
	if (COMPILED.has(key)) return COMPILED.get(key);
	let re = null;
	try { re = new RegExp(anchored(resolved.regex), resolved.flags); } catch (e) { re = null; }
	if (COMPILED.size >= MAX_COMPILED) COMPILED.clear();
	COMPILED.set(key, re);
	return re;
}

/**
 * matchesPattern(value, pattern) -> boolean
 *
 * Does a string fit the pattern (stored, or resolved)? A pattern that
 * resolves to nothing, or does not compile, holds nothing to a rule: true.
 * The web's inline check; the API evaluates through utils/patternRule.js,
 * which adds a time budget, with this same compiled regex.
 */
function matchesPattern(value, pattern) {
	const resolved = resolvePattern(pattern);
	const re = compiled(resolved);
	if (!re) return true;
	return re.test(String(value));
}

/**
 * patternKey(pattern) -> 'preset:<id>' | 'custom:<8 hex>' | null
 *
 * A short, stable name for a pattern that says nothing about where it is
 * used: the admin portal's validation-cost metrics count evaluations under
 * it. A custom pattern's key is the FNV-1a hash of its flags and regex, so
 * two elements with the same regex share one row.
 */
function patternKey(pattern) {
	const resolved = resolvePattern(pattern);
	if (!resolved) return null;
	if (resolved.preset) return 'preset:' + resolved.preset;
	const text = resolved.flags + '/' + resolved.regex;
	let h = 0x811c9dc5;
	for (let i = 0; i < text.length; i++) {
		h ^= text.charCodeAt(i);
		h = Math.imul(h, 0x01000193) >>> 0;
	}
	return 'custom:' + ('0000000' + h.toString(16)).slice(-8);
}

/**
 * jsonSchemaPattern(pattern) -> string | null
 *
 * The anchored regex as JSON Schema's `pattern`, which has no flags: null
 * for a case-insensitive pattern (the schema describes it in words instead).
 */
function jsonSchemaPattern(pattern) {
	const resolved = resolvePattern(pattern);
	if (!resolved || resolved.flags) return null;
	return anchored(resolved.regex);
}

module.exports = {
	MAX_REGEX: MAX_REGEX,
	MAX_MESSAGE: MAX_MESSAGE,
	ALLOWED_FLAGS: ALLOWED_FLAGS,
	MAX_STAR_HEIGHT: MAX_STAR_HEIGHT,
	MAX_REPETITIONS: MAX_REPETITIONS,
	PRESETS: PRESETS,
	presetOf: presetOf,
	anchored: anchored,
	analyzeRegex: analyzeRegex,
	checkSafety: checkSafety,
	MAX_STEPS: MAX_STEPS,
	CAPTURE_STEP: CAPTURE_STEP,
	costOf: costOf,
	checkCost: checkCost,
	checkPattern: checkPattern,
	resolvePattern: resolvePattern,
	compiled: compiled,
	matchesPattern: matchesPattern,
	patternKey: patternKey,
	jsonSchemaPattern: jsonSchemaPattern,
	countChars: countChars,
	// Tests: the tree, and the bound with its limits changed.
	_costInternals: costInternals,
};
